import { ArrowRight } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ConversationFeed, Message, MessageContent, MessageThread } from '@/components/ui';
import { ENTITIES, HOME, MARKUP_SAMPLES, THREADS } from '../conversations/model';
import { Section, Code } from './Section';
import { InboxPatternTiles } from './InboxPatternTiles';
import { PeopleTemplatesTiles } from './PeopleTemplatesTiles';

export function ConversationPatternsSection() {
  const [expanded, setExpanded] = useState(false);
  const [reactions, setReactions] = useState(THREADS[0].message.reactions!);
  const navigate = useNavigate();
  const { hash } = useLocation();
  useEffect(() => {
    if (window.location.hash !== '#conversation-patterns') return;
    const section = document.getElementById('conversation-patterns');
    const main = section?.closest('main');
    const root = main?.parentElement;
    if (!section || !main || !root) return;
    let active = true;
    const align = () =>
      requestAnimationFrame(() => {
        if (!active || window.location.hash !== '#conversation-patterns') return;
        const header = root.querySelector('header')?.getBoundingClientRect().height ?? 0;
        root.scrollTo({
          top:
            root.scrollTop +
            section.getBoundingClientRect().top -
            root.getBoundingClientRect().top -
            header -
            24,
          behavior: 'instant',
        });
      });
    const observer = new ResizeObserver(align);
    const stop = () => {
      active = false;
      observer.disconnect();
      root.removeEventListener('scroll', align);
      for (const event of ['wheel', 'pointerdown', 'keydown']) root.removeEventListener(event, stop, true);
    };
    observer.observe(main);
    root.addEventListener('scroll', align, { passive: true });
    for (const event of ['wheel', 'pointerdown', 'keydown']) root.addEventListener(event, stop, true);
    align();
    void document.fonts.ready.then(align);
    return stop;
  }, [hash]);
  return (
    <Section
      id="conversation-patterns"
      title="Переписка"
      lead="Общие сообщения для любых каналов: ветки, цитаты, реакции и безопасный показ внешней разметки. Вымышленные данные."
    >
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <div className="flex flex-wrap items-center gap-4 border-b border-line p-5">
          <div className="min-w-0 flex-1">
            <h3 className="type-heading">Лента и ветки</h3>
            <p className="mt-1 text-sm text-fg-muted">
              Автор, время, источник, вложения, прочтение и подгрузка той же формы.
            </p>
          </div>
          <Link
            to={HOME}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-muted px-3 text-xs font-semibold text-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Открыть образец
            <ArrowRight className="size-4" />
          </Link>
        </div>
        <div data-slot="conversation-preview" className="max-h-96 overflow-y-auto">
          <ConversationFeed label="Пример ленты переписки">
            <li>
              <MessageThread
                thread={{ ...THREADS[0], message: { ...THREADS[0].message, reactions } }}
                expanded={expanded}
                onExpandedChange={setExpanded}
                entities={ENTITIES}
                onEntityOpen={(entity) => navigate(HOME + '/entity/' + entity.id)}
                onReaction={(_, id) =>
                  setReactions((current) =>
                    current.map((reaction) =>
                      reaction.id === id
                        ? {
                            ...reaction,
                            selected: !reaction.selected,
                            count: reaction.count + (reaction.selected ? -1 : 1),
                            people: reaction.selected
                              ? reaction.people?.filter((name) => name !== 'Демо Пользователь')
                              : [...(reaction.people ?? []), 'Демо Пользователь'],
                          }
                        : reaction,
                    ),
                  )
                }
              />
            </li>
            <li>
              <Message
                message={THREADS[1].message}
                entities={ENTITIES}
                onEntityOpen={(entity) => navigate(HOME + '/entity/' + entity.id)}
              />
            </li>
          </ConversationFeed>
        </div>
        <p className="border-t border-line p-4">
          <Code>Message · MessageThread · ConversationFeed · MessageSkeleton · FileChip</Code>
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <div className="flex flex-wrap items-center gap-4 border-b border-line p-5">
          <div className="min-w-0 flex-1">
            <h3 className="type-heading">Разметка сообщения</h3>
            <p className="mt-1 text-sm text-fg-muted">
              Markdown, компактный формат чата и обычный текст. Один показ для переписки и комментариев.
            </p>
          </div>
          <Link
            to={HOME + '?demo=markup'}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-muted px-3 text-xs font-semibold text-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Все варианты
            <ArrowRight className="size-4" />
          </Link>
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-6 p-5 lg:grid-cols-2">
          {MARKUP_SAMPLES.filter((_, index) => index === 0 || index === 2).map((sample) => (
            <div key={sample.title} className="min-w-0">
              <p className="mb-3 type-overline text-fg-muted">{sample.description}</p>
              <MessageContent
                content={sample.content}
                entities={ENTITIES}
                onEntityOpen={(entity) => navigate(HOME + '/entity/' + entity.id)}
              />
            </div>
          ))}
        </div>
        <p className="border-t border-line p-4">
          <Code>MessageContent · MessageCodeBlock · parseMessageContent</Code>
        </p>
      </div>
      <InboxPatternTiles />
      <PeopleTemplatesTiles />
    </Section>
  );
}
